"use client";

import { useRouter } from "next/navigation";
import type React from "react";
import { createContext, useContext } from "react";
import en from "~/dictionaries/en";
import sv from "~/dictionaries/sv";

interface TranslationContextProps {
  dictionary: Dictionary;
  setLocale: (locale: AppLocale) => void;
  lang: AppLocale; // Added current locale
}

const TranslationContext = createContext<TranslationContextProps>({
  dictionary: {} as Dictionary,
  setLocale: () => {},
  lang: "sv" as AppLocale, // Default value
});

type Props = {
  children: React.ReactNode;
  lang: AppLocale;
};

/**
 * `lang` comes in as a prop from the layout's route params rather than from
 * `usePathname()`: reading the pathname (or search params) during render
 * makes this provider — and everything under it — a dynamic hole in the
 * prerendered shell, which shipped every page as empty HTML. Handlers read
 * `window.location` instead; they only ever run in the browser.
 */
export default function TranslationProvider({ children, lang }: Props) {
  const router = useRouter();
  const dictionary = lang === "sv" ? sv : en;

  const setLocale = (locale: AppLocale) => {
    const pathName = window.location.pathname;
    const newPath = pathName.startsWith(`/${lang}`)
      ? pathName.replace(`/${lang}`, `/${locale}`)
      : `/${locale}${pathName}`;

    const searchParamsString = window.location.search.replace(/^\?/, "");
    const newUrl = searchParamsString
      ? `${newPath}?${searchParamsString}`
      : newPath;

    // Navigate to the new path
    router.push(newUrl);
  };

  return (
    <TranslationContext.Provider value={{ dictionary, setLocale, lang }}>
      {children}
    </TranslationContext.Provider>
  );
}

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  return context;
};
